import {
  AppShell,
  TreeNodeData,
  Group,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Box,
  Button,
  Tabs,
  Divider,
  Title,
  Center,
  Accordion,
  Skeleton,
  ThemeIcon,
} from "@mantine/core";
import {
  IconClick,
  IconReport,
  IconSearch,
  IconSettings,
  IconLayersIntersect,
  IconInfoCircle,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { useState, useEffect, useMemo } from "react";
import { Location } from "../../types/Location";
import { LayerObject, AreaSection } from "../../types/Polygon";
// OBSOLETE: GetCentreCoordinates no longer used - replaced by extent-based zoom
// import { GetCentreCoordinates } from "../../utilities/Utilities";
import { zoomToGeometryExtent } from "../../utilities/MapUtils";
import FeatureMapper from "../../components/layers/FeatureMapper";
import Layers from "../../components/layers/Layers";
import LocationTree from "../../components/tree/locationTree";
import StatusLegend from "../../components/legend/StatusLegend";
import MapViewer from "../../components/mapViewer/mapViewer";
import MapToolbar from "../../components/mapViewer/MapToolbar";
import ObjectForm from "../../components/generic/ObjectForm";
import useTransientStore from "../../data/transcientStore";
import { useDisclosure } from "@mantine/hooks";
import { BaseGeometry } from "../../types/BaseGeometry";
import SearchMenu from "../../components/menu/SearchMenu";
import AddActionMenu from "../../components/menu/AddActionMenu";
import { ObjectData } from "../../components/generic/types/ObjectFormTypes";
import { convertGeometryToObjectData, convertObjectDataToBaseGeometry } from "../../utilities/GeometryConverters";
import { useTranslation } from "../../hooks/useTranslation";
// Obsolete: Bulk geometry fetching removed - now using search-driven loading via SearchMenu
// import { 
//   getAllPolylinesByAddressSystem,
//   getAllPolygonsByAddressSystem,
//   getAllPolypointsByAddressSystem
// } from "../../services/MapComponentsService";

// Work Step interface
interface WorkStep {
  id: number;
  typeOfCoating: string;
  unit: string;
  amount: number;
  surfaceArea: number;
  tons: number;
  unitPrice: number;
  priceC: number;
}

interface MapPanelProps {
  polygonSections: AreaSection[];
  treeData: TreeNodeData[];
  setTreeData: (data: TreeNodeData[]) => void;
  currentLocation: Location;
  projection: string;
}

export default function MapPanel({
  polygonSections,
  treeData,
  setTreeData,
  currentLocation,
  projection,
}: Readonly<MapPanelProps>) {
  const addrIdFromStore = useTransientStore((state) => state.selectedAddressSystem?.id);
  const { getTranslation } = useTranslation({ pageId: 317, addressSystemId: addrIdFromStore || 0, autoLoad: true });
  //#region Hooks

  // Removed unused iconStyle variable
  const [currentNode, setCurrentNode] = useState<TreeNodeData | null>(null);
  // OBSOLETE: Map centering now handled by map.getView().fit(extent) directly in handleFeatureSelectWithData
  // const [mapCoords, setMapCoords] = useState<[number, number]>(
  //   currentLocation.coordinates
  // );
  // const [zoomLevel, setZoomLevel] = useState<number>(15);
  // // Persist original map position/zoom for reliable resets
  // const initialCenterRef = useRef<[number, number]>(
  //   currentLocation.coordinates
  // );
  // const initialZoomRef = useRef<number>(15);
  // const [shouldCenterMap, setShouldCenterMap] = useState<boolean>(false); // New state to control centering
  const [formOpen, setFormOpen] = useState(false);
  const [filteredMaintenanceSections, setFilteredMaintenanceSections] =
    useState<AreaSection[]>([]);
  const [tilelayer, setTilelayer] = useState<"osm" | "satellite">("osm");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Use shared store for selectedGeometry (accessible by both mapPanel and SearchMenu)
  const selectedGeometry = useTransientStore((state) => state.selectedGeometry);
  const setSelectedGeometry = useTransientStore((state) => state.setSelectedGeometry);
  
  // Removed hoverGeometryId (name used directly for badge)
  const [hoverGeometryName, setHoverGeometryName] = useState<string | null>(
    null
  ); // derived name for hover badge
  // State for geometry selection menu
  const [geometryMenuOpen, setGeometryMenuOpen] = useState(false);
  const [geometryMenuItems, setGeometryMenuItems] = useState<BaseGeometry[]>([]);
  const [geometryMenuPosition, setGeometryMenuPosition] = useState<[number, number]>([0, 0]);
  // State for drawing mode
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  // Removed double-click tracking ref (reverting to single-click open)
  // Right navbar (actions & tools)
  const [actionTab, setActionTab] = useState<string>("search-actions");
  const [rightNavCollapsed, setRightNavCollapsed] = useState<boolean>(false);
  // Removed Show/Hide All layers state (expandAllSignal) and search filter clearing logic
  const loadingOverlayVisible = useTransientStore(
    (state) => state.loadingOverlayVisible
  );
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem
  );
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const triggerClearDrawing = useTransientStore((state) => state.triggerClearDrawing);

  const [opened, { open, close }] = useDisclosure(false);

  // Future enhancement: derive center coordinates from selected area/section instead of WKT centroid
  // useEffect(() => {
  //   if (formOpen && currentNode?.nodeProps?.object?.WKT) {
  //     const newCoordinates = GetCentreCoordinates(
  //       currentNode.nodeProps?.object?.WKT
  //     );
  //     if (newCoordinates) {
  //       setMapCoords(newCoordinates);
  //       setZoomLevel(17);
  //       setShouldCenterMap(true);
  //       const obj = currentNode.nodeProps?.object;
  //       const geometry: BaseGeometry = {
  //         // Core properties
  //         id: obj?.Id || 0,
  //         name: obj?.Name || (typeof currentNode.label === "string" ? currentNode.label : ""),
  //         wkt: obj?.WKT || "",
  //         visible: currentNode.nodeProps?.visible || false,
  //         featureId: obj?.LocalID ? Number.parseInt(obj.LocalID) : 0,
  //         addressSystemId: obj?.addressSystemId || 0,
  //         contractId: obj?.contractId || 0,
  //         contractName: obj?.contractName || "",
  //         startDate: obj?.startDate || null,
  //         stateId: obj?.stateId || 0,
  //         streetName: obj?.streetName || "",
  //         startDistance: obj?.startDistance || 0,
  //         endDistance: obj?.endDistance || 0,

  //         // Extended properties from PolylineDto
  //         featId: obj?.featId,
  //         typeId: obj?.typeId,
  //         strengthId: obj?.strengthId,
  //         typeDesc: obj?.typeDesc,
  //         statusDesc: obj?.statusDesc,
  //         orderNumber: obj?.orderNumber,
  //         networkNumber: obj?.networkNumber,
  //         siteNumber: obj?.siteNumber,
  //         typeComment: obj?.typeComment,
  //         lanes: obj?.lanes,
  //         foremanId: obj?.foremanId,
  //         foremanName: obj?.foremanName,
  //         contractorForemanId: obj?.contractorForemanId,
  //         projectManagerId: obj?.projectManagerId,
  //         subscriberId: obj?.subscriberId,
  //         subscriberName: obj?.subscriberName,
  //         qualityControlId: obj?.qualityControlId,
  //         qualityControlId2: obj?.qualityControlId2,
  //         extraText1: obj?.extraText1,
  //         hasRoadMarkings: obj?.hasRoadMarkings,
  //         objectLen: obj?.objectLen,
  //         plannedDate: obj?.plannedDate,
  //         readyDate: obj?.readyDate,
  //         approvedDate: obj?.approvedDate,
  //         description: obj?.description,
  //         guaranteeFollowId: obj?.guaranteeFollowId,
  //         extraText2: obj?.extraText2,
  //         publishToApi: obj?.publishToApi,
  //         goals: obj?.goals,
  //         roleAccessInUse: obj?.roleAccessInUse,
  //         rolePermissions: obj?.rolePermissions,
  //         files: obj?.files,
  //         qcFiles: obj?.qcFiles,
  //         modifier: obj?.modifier,
  //         modified: obj?.modified,
  //         checkedToInvoice: obj?.checkedToInvoice,
  //         checkedToInvoiceDate: obj?.checkedToInvoiceDate,
  //         checkedToInvoiceUserName: obj?.checkedToInvoiceUserName,
  //       };
  //       setSelectedGeometry(geometry);
  //       open();
  //     }
  //   } else if (!formOpen) {
  //     setShouldCenterMap(false);
  //   }
  // }, [currentLocation.coordinates, currentNode, formOpen, open]);

  // OBSOLETE: Reset the centering flag after it's been used - no longer needed with extent-based zoom
  // useEffect(() => {
  //   if (shouldCenterMap) {
  //     const timer = setTimeout(() => {
  //       setShouldCenterMap(false);
  //     }, 100); // Small delay to ensure the map has time to center

  //     return () => clearTimeout(timer);
  //   }
  // }, [shouldCenterMap]);

  //Use effect hook to loop through all nodes in the tree and only add visible maintenance sections to the filtered array
  useEffect(() => {
    const collected: AreaSection[] = [];

    const collectVisibleSections = (nodes: TreeNodeData[]): void => {
      for (const node of nodes) {
        const layerType = node.nodeProps?.object?.LayerType as
          | LayerObject
          | undefined;
        const isVisible = node.nodeProps?.visible;
        if (isVisible && layerType === LayerObject.MaintenanceSection) {
          const id = node.nodeProps?.object?.Id;
          const section = polygonSections.find((ms) => ms.LocalID === id);
          if (section) collected.push(section);
        }
        if (node.children?.length) collectVisibleSections(node.children);
      }
    };

    collectVisibleSections(treeData);
    setFilteredMaintenanceSections(collected);
  }, [polygonSections, treeData]);

  //#endregion
  //#region Functions

  // Removed bulk visibility helpers (makeAllVisible/makeAllHidden) and toggle handler

  /**
   * Updates a specific node in a tree structure with new data.
   *
   * @param nodes - The array of tree nodes to search through.
   * @param nodeToUpdate - The node containing updated data to replace the existing node.
   * @returns A new array of tree nodes with the specified node updated.
   */
  // function updateNodeInTree(
  //   nodes: TreeNodeData[],
  //   nodeToUpdate: TreeNodeData
  // ): TreeNodeData[] {
  //   return nodes.map((node): TreeNodeData => {
  //     if (
  //       node.nodeProps?.object?.LocalID ===
  //       nodeToUpdate.nodeProps?.object?.LocalID
  //     ) {
  //       return nodeToUpdate;
  //     }
  //     if (node.children)
  //       node.children = updateNodeInTree(node.children, nodeToUpdate);
  //     return node;
  //   });
  // }

  const handleLayerSwitch = () => {
    setTilelayer((prevLayer) => (prevLayer === "osm" ? "satellite" : "osm"));
  };

  // Removed node counts (previously used for collapsed summary) as they are no longer displayed.

  /**
   * Handles the submission of updated geometry data from the ObjectForm modal.
   * @param updatedObjectData - The updated object data from the form
   */
  const handleGeometryUpdate = (updatedObjectData: ObjectData) => {
    if (selectedGeometry) {
      const updatedBaseGeometry = convertObjectDataToBaseGeometry(
        updatedObjectData,
        selectedGeometry
      );
      setSelectedGeometry(updatedBaseGeometry);
    }
  };

  /**
   * Handles map background clicks (clicking empty map space).
   * Blocked when modal is minimized or drawing is active.
   */
  const handleMapBackgroundClick = () => {
    const { isModalMinimized } = useTransientStore.getState();
    if (isDrawingActive || isModalMinimized) return;
    handleModalClose();
  };

  /**
   * Handles closing the ObjectForm modal.
   * Always closes regardless of state (used by X button).
   */
  const handleModalClose = () => {
    close();
    setFormOpen(false);
    setSelectedGeometry(null);
  };

  // Clears current selection and resets map to its original position without reopening the form
  const clearSelection = () => {
    // Close any open form/modal
    close();
    setFormOpen(false);
    // Reset selection-related state & any hover/labels
    setSelectedGeometry(null);
    setCurrentNode(null);
    setHoverGeometryName(null);
    // OBSOLETE: Map reset logic - no longer needed with extent-based zoom
    // setMapCoords(initialCenterRef.current);
    // setZoomLevel(initialZoomRef.current);
    // setShouldCenterMap(true);
  };

  // Close geometry menu when clicking outside
  useEffect(() => {
    if (!geometryMenuOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is outside the menu
      if (!target.closest('[data-geometry-menu]')) {
        setGeometryMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [geometryMenuOpen]);

  //#endregion

  //#region UI Elements

  // Helper to locate a node by LocalID across full tree
  // const findNodeByLocalId = (
  //   localId: string,
  //   nodes: TreeNodeData[]
  // ): TreeNodeData | null => {
  //   const target = String(localId);
  //   for (const n of nodes) {
  //     const nodeLocal = n.nodeProps?.object?.LocalID;
  //     if (nodeLocal != null && String(nodeLocal) === target) return n;
  //     if (n.children?.length) {
  //       const found = findNodeByLocalId(localId, n.children);
  //       if (found) return found;
  //     }
  //   }
  //   return null;
  // };

  const mapView = (
    <MapViewer
      coordinates={currentLocation.coordinates} // OBSOLETE props commented below - using extent-based zoom instead
      projection={projection}
      zoom={15} // OBSOLETE: was {zoomLevel}
      tileLayer={tilelayer}
      centerOnCoordinates={false} // OBSOLETE: was {shouldCenterMap}
    >
      <>
        <Layers>
          <FeatureMapper
            onMapBackgroundClick={handleMapBackgroundClick}
            onFeatureSelectWithData={handleFeatureSelectWithData}
            onHoverFeature={(geometryData, allOverlapping = []) => {
              // Only show hover when not drawing
              if (!isDrawingActive) {
                // Display count for multiple geometries, name for single
                if (allOverlapping.length > 1) {
                  setHoverGeometryName(`${allOverlapping.length} overlapping objects (Click to Select)`);
                } else if (geometryData) {
                  setHoverGeometryName(geometryData.name || null);
                } else {
                  setHoverGeometryName(null);
                }
              }
            }}
            onMultipleGeometriesClick={(geometries, clickPixel) => {
              setGeometryMenuItems(geometries);
              setGeometryMenuPosition(clickPixel);
              setGeometryMenuOpen(true);
            }}
            isDrawing={isDrawingActive}
            // visibleGeometries={(() => {
            //   // Extract all visible non-maintenance geometries from treeData
            //   const list: BaseGeometry[] = [];
            //   const walk = (nodes: TreeNodeData[]) => {
            //     for (const n of nodes) {
            //       const obj: any = n.nodeProps?.object;
            //       if (n.nodeProps?.visible && obj?.WKT) {
            //         list.push({
            //           id: obj.Id || 0,
            //           name:
            //             obj.Name ||
            //             (typeof n.label === "string" ? n.label : ""),
            //           wkt: obj.WKT,
            //           visible: true,
            //           featureId: obj.LocalID
            //             ? Number.parseInt(obj.LocalID)
            //             : obj.Id || 0,
            //           addressSystemId: obj.addressSystemId || 0,
            //           contractId: obj.contractId || 0,
            //           contractName: obj.contractName || "",
            //           streetName: obj.streetName || "",
            //           startDate: obj.startDate || null,
            //           stateId: obj.stateId || 0,
            //           startDistance: obj.startDistance || 0,
            //           endDistance: obj.endDistance || 0,
            //           foremanId: obj.nodeProps?.object?.foremanId || 0,
            //           foremanName: obj.nodeProps?.object?.foremanName || "",
            //           qualityControlId: obj.nodeProps?.object?.qualityControlId || 0,
            //           qualityControlId2: obj.nodeProps?.object?.qualityControlId2 || 0,
            //           description: obj.nodeProps?.object?.description || "",
            //         });
            //       }
            //       if (n.children?.length) walk(n.children);
            //     }
            //   };
            //   walk(treeData);
            //   return list;
            // })()}
            selectedGeometry={selectedGeometry}
            // OBSOLETE: Auto-centering now handled by extent-based zoom in handleFeatureSelectWithData
            // autoCenterFeature={(geom) => {
            //   // Center and zoom when a geometry is newly selected if not already triggered by form open
            //   if (geom?.wkt) {
            //     const coords = GetCentreCoordinates(geom.wkt);
            //     if (coords) {
            //       setMapCoords(coords);
            //       setZoomLevel(17);
            //     }
            //   }
            // }}
          />
        </Layers>
        <MapToolbar
          initialCenter={currentLocation.coordinates} // OBSOLETE: was {mapCoords}
          initialZoom={15} // OBSOLETE: was {zoomLevel}
          selectedGeometryWkt={selectedGeometry?.wkt || null}
          onReset={clearSelection}
          onToggleBaseLayer={handleLayerSwitch}
          currentBaseLayer={tilelayer}
        />
        {/* Hover preview badge */}
        {hoverGeometryName && !opened && (
          <div
            style={{
              position: "absolute",
              bottom: 200,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,0.65)",
              color: "#fff",
              padding: "8px 16px",
              borderRadius: 8,
              fontSize: 14,
              pointerEvents: "none",
            }}
          >
            {/* Object: {hoverGeometryName} */}
            {hoverGeometryName}
          </div>
        )}
        {/* Geometry selection menu */}
        {geometryMenuOpen && (
          <div
            data-geometry-menu
            style={{
              position: "absolute",
              top: geometryMenuPosition[1],
              left: geometryMenuPosition[0],
              background: "rgba(70, 70, 70, 0.85)",
              borderRadius: 8,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
              minWidth: 260,
              maxWidth: 400,
              maxHeight: 400,
              overflow: "hidden",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                fontWeight: 500,
                fontSize: 14,
                color: "#fff",
                textAlign: "center",
              }}
            >
              Select Object ({geometryMenuItems.length})
            </div>
            <div style={{ maxHeight: "320px", overflow: "auto" }}>
              {geometryMenuItems.map((geom, idx) => (
                <div
                  key={`${geom.id}-${idx}`}
                  onClick={() => {
                    handleFeatureSelectWithData(geom);
                    setGeometryMenuOpen(false);
                  }}
                  style={{
                    padding: "10px 16px",
                    cursor: "pointer",
                    borderBottom:
                      idx < geometryMenuItems.length - 1
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "none",
                    fontSize: 13,
                    transition: "background 0.15s ease",
                    color: "#fff",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 3 }}>
                    {geom.name}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.9)" }}>
                    {(() => {
                      const wkt = geom.wkt?.toUpperCase() || "";
                      if (wkt.includes("POLYGON")) return "Polygon";
                      if (wkt.includes("LINE")) return "Line";
                      return "Point";
                    })()}
                  </div>
                  {geom.contractName && (
                    <div style={{ fontSize: 12, color: "rgba(255, 255, 255, 0.9)" }}>
                      {geom.contractName}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div
              onClick={() => setGeometryMenuOpen(false)}
              style={{
                padding: "8px 16px",
                textAlign: "center",
                cursor: "pointer",
                fontSize: 12,
                color: "rgba(255, 255, 255, 0.7)",
                borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)";
              }}
            >
              Cancel
            </div>
          </div>
        )}
      </>
    </MapViewer>
  );

  //#endregion

  // Stop drawing when switching tabs
  const handleTabChange = (newTab: string | null) => {
    if (!newTab) return;
    
    // Stop drawing and clear geometry when switching tabs
    if (isDrawingActive) {
      setIsDrawingActive(false);
    }
    triggerClearDrawing(); // Trigger Zustand to clear the drawing layer
    
    setActionTab(newTab);
  };

  // Action / utility tabs on the (legacy) right panel – refactored to config driven structure
  const ACTION_TABS = useMemo(
    () => [
      {
        value: "search-actions",
        title: "Search Actions",
        shortLabel: "Search",
        icon: IconSearch,
        content: <SearchMenu />,
      },
      {
        value: "add-action",
        title: "Add Action",
        shortLabel: "Add",
        icon: IconClick,
        content: <AddActionMenu onDrawingStateChange={setIsDrawingActive} />,
      },
      {
        value: "reports",
        title: "Reports",
        shortLabel: "Reports",
        icon: IconReport,
        content: (
          <Box p="md">
            <Button fullWidth variant="light">
              View Reports
            </Button>
          </Box>
        ),
      },
      {
        value: "settings",
        title: "Settings",
        shortLabel: "Settings",
        icon: IconSettings,
        content: (
          <Box p="md">
            <Button fullWidth variant="light">
              View Settings
            </Button>
          </Box>
        ),
      },
    ],
    []
  );

  // // Helper for feature selection (single-click opens form)
  // function handleFeatureSelect(localId: string | null) {
  //   if (!localId) return;
  //   const node = findNodeByLocalId(localId, treeData);
  //   const obj: any = node?.nodeProps?.object;
  //   if (!obj?.WKT) return;
  //   const geometry: BaseGeometry = {
  //     // Core properties
  //     id: obj.Id || 0,
  //     name: obj.Name || (typeof node?.label === "string" ? node.label : ""),
  //     wkt: obj.WKT,
  //     visible: node?.nodeProps?.visible || false,
  //     featureId: obj.LocalID ? Number.parseInt(obj.LocalID) : obj.Id || 0,
  //     addressSystemId: obj.addressSystemId || 0,
  //     contractId: obj.contractId || 0,
  //     contractName: obj.contractName || "",
  //     startDate: obj.startDate || null,
  //     stateId: obj.stateId || 0,
  //     streetName: obj.streetName || "",
  //     startDistance: obj.startDistance || 0,
  //     endDistance: obj.endDistance || 0,

  //     // Extended properties
  //     featId: obj.featId,
  //     typeId: obj.typeId,
  //     strengthId: obj.strengthId,
  //     typeDesc: obj.typeDesc,
  //     statusDesc: obj.statusDesc,
  //     orderNumber: obj.orderNumber,
  //     networkNumber: obj.networkNumber,
  //     siteNumber: obj.siteNumber,
  //     typeComment: obj.typeComment,
  //     lanes: obj.lanes,
  //     foremanId: obj.foremanId,
  //     foremanName: obj.foremanName,
  //     contractorForemanId: obj.contractorForemanId,
  //     projectManagerId: obj.projectManagerId,
  //     subscriberId: obj.subscriberId,
  //     subscriberName: obj.subscriberName,
  //     qualityControlId: obj.qualityControlId,
  //     qualityControlId2: obj.qualityControlId2,
  //     extraText1: obj.extraText1,
  //     hasRoadMarkings: obj.hasRoadMarkings,
  //     objectLen: obj.objectLen,
  //     plannedDate: obj.plannedDate,
  //     readyDate: obj.readyDate,
  //     approvedDate: obj.approvedDate,
  //     description: obj.description,
  //     guaranteeFollowId: obj.guaranteeFollowId,
  //     extraText2: obj.extraText2,
  //     publishToApi: obj.publishToApi,
  //     goals: obj.goals,
  //     roleAccessInUse: obj.roleAccessInUse,
  //     rolePermissions: obj.rolePermissions,
  //     files: obj.files,
  //     qcFiles: obj.qcFiles,
  //     drillLocation: obj.drillLocation,
  //     modifier: obj.modifier,
  //     modified: obj.modified,
  //     checkedToInvoice: obj.checkedToInvoice,
  //     checkedToInvoiceDate: obj.checkedToInvoiceDate,
  //     checkedToInvoiceUserName: obj.checkedToInvoiceUserName,
  //   };
  //   setSelectedGeometry(geometry);
  //   setFormOpen(true);
  //   open();
  //   const coords = GetCentreCoordinates(obj.WKT);
  //   if (coords) {
  //     setMapCoords(coords);
  //     setZoomLevel(17);
  //   }
  // }

  // Handler for when feature is clicked with full geometry data from map
  function handleFeatureSelectWithData(geometryData: BaseGeometry | null) {
    if (!geometryData) return;

    // Try to find the full geometry data from the store
    // This ensures we have complete data (contract name, status desc, foreman name, etc.)
    const store = useTransientStore.getState();
    let fullGeometry: BaseGeometry = geometryData;

    // Determine geometry type from WKT and look up in appropriate store
    const wkt = geometryData.wkt?.toUpperCase() || "";
    
    if (wkt.includes("POLYGON")) {
      const fullPolygon = store.currentPolygons.find(
        p => p.id === geometryData.id && p.featureId === geometryData.featureId
      );
      if (fullPolygon) {
        fullGeometry = fullPolygon as unknown as BaseGeometry;
      }
    } else if (wkt.includes("LINE")) {
      const fullPolyline = store.currentPolylines.find(
        p => p.id === geometryData.id && p.featureId === geometryData.featureId
      );
      if (fullPolyline) {
        fullGeometry = fullPolyline as unknown as BaseGeometry;
      }
    } else {
      // Point
      const fullPoint = store.currentPolypoints.find(
        p => p.id === geometryData.id && p.featureId === geometryData.featureId
      );
      if (fullPoint) {
        fullGeometry = fullPoint as unknown as BaseGeometry;
      }
    }

    // Use the full geometry data (falls back to geometryData if not found in store)
    setSelectedGeometry(fullGeometry);
    setFormOpen(true);
    open();

    // Fit to geometry extent instead of fixed zoom
    const map = store.map;
    zoomToGeometryExtent(map, geometryData.wkt);
  }

  return (
    <>
      <AppShell.Aside
        style={{
          backgroundColor: "White",
          borderRight: "1px solid #e0e0e0",
          zIndex: 2,
          width: sidebarCollapsed ? 60 : 385,
          transition: "width 180ms ease",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
        }}
      >
        <Box style={{ flexShrink: 0, padding: "8px 10px 4px 10px" }}>
          <Group justify="space-between" gap="xs">
            <Tooltip
              label={sidebarCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <ActionIcon
                variant="subtle"
                onClick={() => setSidebarCollapsed((v) => !v)}
              >
                {sidebarCollapsed ? (
                  <IconChevronLeft size={18} />
                ) : (
                  <IconChevronRight size={18} />
                )}
              </ActionIcon>
            </Tooltip>
            {/* {!sidebarCollapsed && (
              <Title order={5} style={{ fontWeight: 600 }}>
                Map Tools
              </Title>
            )} */}
          </Group>
        </Box>

        {!sidebarCollapsed && (
          <Accordion
            multiple
            variant="separated"
            defaultValue={["layers"]}
            px="8px"
            styles={{ content: { padding: "0 4px 8px 4px" } }}
          >
            <Accordion.Item value="details">
              <Accordion.Control
                icon={
                  <ThemeIcon size="sm" variant="light" color="teal">
                    <IconInfoCircle size={14} />
                  </ThemeIcon>
                }
              >
                Selection Details
              </Accordion.Control>
              <Accordion.Panel>
                {selectedGeometry ? (
                  <Box>
                    <Title order={6} style={{ lineHeight: 1.2 }}>
                      {selectedGeometry?.name}
                    </Title>
                    <Box style={{ fontSize: "11px", color: "#555" }}>
                      ID: {selectedGeometry?.id}
                    </Box>
                    {selectedGeometry?.streetName && (
                      <Box style={{ fontSize: "11px", color: "#555" }}>
                        Street: {selectedGeometry?.streetName}
                      </Box>
                    )}
                    {selectedGeometry?.contractName && (
                      <Box style={{ fontSize: "11px", color: "#555" }}>
                        Contract: {selectedGeometry?.contractName}
                      </Box>
                    )}
                    <Group mt="xs" gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => {
                          // Zoom to extent when center button is clicked (useful when ObjectForm is minimized and map has been panned)
                          const map = useTransientStore.getState().map;
                          zoomToGeometryExtent(map, selectedGeometry?.wkt);
                        }}
                      >
                        Center
                      </Button>
                      <Button
                        size="xs"
                        variant="default"
                        onClick={clearSelection}
                      >
                        Clear
                      </Button>
                    </Group>
                  </Box>
                ) : (
                  <Box
                    style={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      color: "#666",
                    }}
                  >
                    Nothing selected
                  </Box>
                )}
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item value="layers">
              <Accordion.Control
                icon={
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconLayersIntersect size={14} />
                  </ThemeIcon>
                }
              >
                Legends
              </Accordion.Control>
              <Accordion.Panel>
                <Divider
                  my={4}
                  variant="dashed"
                  label={getTranslation("LABEL_GEOMETRY_STATUS_COLORS") || "Geometry Status Colors"}
                />
                <ScrollArea.Autosize
                  mah={window.screen.availHeight * 0.65}
                  offsetScrollbars
                  scrollbarSize={8}
                >
                  <Box mb="md">
                    <StatusLegend withPaper={false} />
                  </Box>

                  <Divider
                    my="md"
                    label={getTranslation("LABEL_STATIC_LAYERS") || "Static Layers"}
                    labelPosition="center"
                  />

                  {(() => {
                    const empty =
                      !treeData ||
                      (Array.isArray(treeData) && treeData.length === 0);
                    if (empty) {
                      if (loadingOverlayVisible) {
                        return (
                          <Box p="xs">
                            <Skeleton height={14} mt={6} radius="xl" />
                            <Skeleton height={14} mt={6} radius="xl" />
                            <Skeleton
                              height={14}
                              mt={6}
                              width="70%"
                              radius="xl"
                            />
                          </Box>
                        );
                      }
                      return (
                        <Center>
                          <Box
                            p="xs"
                            style={{
                              fontSize: "11px",
                              fontStyle: "italic",
                              color: "#666",
                            }}
                          >
                            No static layers loaded
                          </Box>
                        </Center>
                      );
                    }
                    return (
                      // <div>tree</div>
                      <LocationTree
                        data={treeData}
                        updateTreeData={setTreeData}
                        setCurrentNode={setCurrentNode}
                        setFormOpen={setFormOpen}
                      />
                    );
                  })()}
                </ScrollArea.Autosize>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}

        {/* Removed collapsed sidebar vertical 'Layers' label and counts as requested */}
      </AppShell.Aside>

      {/* Right Navbar reinstated for Actions & Tools */}
      <AppShell.Navbar
        visibleFrom="md"
        style={{
          borderLeft: "1px solid #e0e0e0",
          width: rightNavCollapsed ? 50 : 500,
          transition: "width 160ms ease",
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
        }}
      >
        <Tabs
          value={actionTab}
          onChange={handleTabChange}
          variant={rightNavCollapsed ? "outline" : "pills"}
          radius="md"
          orientation={rightNavCollapsed ? "vertical" : "horizontal"}
          keepMounted={true}
          style={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          {rightNavCollapsed ? (
            <Box px={4} py={8}>
              <Group
                gap={4}
                justify="flex-start"
                align="stretch"
                mb="xs"
                style={{ flexDirection: "column" }}
              >
                <Tooltip label={getTranslation("LABEL_EXPAND_PANEL") || "Expand panel"}>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => setRightNavCollapsed((v) => !v)}
                  >
                    <IconChevronRight size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Group
                gap={4}
                justify="flex-start"
                align="stretch"
                style={{ flexDirection: "column" }}
              >
                {ACTION_TABS.map((tab) => {
                  const IconComp = tab.icon;
                  return (
                    <Tooltip key={tab.value} label={tab.title} position="left">
                      <ActionIcon
                        variant={actionTab === tab.value ? "filled" : "light"}
                        size="lg"
                        onClick={() => handleTabChange(tab.value)}
                      >
                        <IconComp size={18} />
                      </ActionIcon>
                    </Tooltip>
                  );
                })}
              </Group>
            </Box>
          ) : (
            <>
              <Box px="sm" pt="sm" pb="xs">
                <Group justify="center" align="center" gap="xs" pos="relative">
                  <Tabs.List justify="center" style={{ flexWrap: "nowrap" }}>
                    {ACTION_TABS.map((tab) => {
                      const IconComp = tab.icon;
                      return (
                        <Tooltip
                          key={tab.value}
                          label={tab.title}
                          position="bottom"
                        >
                          <Tabs.Tab
                            value={tab.value}
                            color="blue"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "12px 16px",
                            }}
                          >
                            <IconComp size={24} />
                          </Tabs.Tab>
                        </Tooltip>
                      );
                    })}
                  </Tabs.List>
                  <Tooltip label={getTranslation("LABEL_COLLAPSE_PANEL") || "Collapse panel"}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => setRightNavCollapsed((v) => !v)}
                      style={{ position: "absolute", right: 0 }}
                    >
                      <IconChevronLeft size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Box>
              <Divider />
              <Box
                style={{
                  flex: 1,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {ACTION_TABS.map((tab) => (
                  <Tabs.Panel
                    key={tab.value}
                    value={tab.value}
                    pt="xs"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <Box px="sm" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>{tab.content}</Box>
                  </Tabs.Panel>
                ))}
              </Box>
            </>
          )}
        </Tabs>
      </AppShell.Navbar>

      <AppShell.Main p={0}>
        {(() => {
          const leftWidth = sidebarCollapsed ? 60 : 385;
          const rightWidth = rightNavCollapsed ? 60 : 360;
          return (
            <div
              style={{
                height: "calc(100vh)",
                // position: 'fixed',
                top: "60px",
                bottom: 0,
                zIndex: 1,
                left: leftWidth,
                right: rightWidth,
                transition: "left 180ms ease, right 180ms ease",
              }}
              className="map-container-responsive"
            >
              {mapView}
            </div>
          );
        })()}
        <style>{`
                    @media (max-width: 767px) {
                        .map-container-responsive {
                            left: 0 !important;
                            right: 0 !important;
                        }
                    }
                `}</style>
        {selectedGeometry && (
          <ObjectForm
            opened={opened}
            onClose={handleModalClose}
            onDrawingStateChange={(isDrawing) => setIsDrawingActive(isDrawing)}
            objectData={convertGeometryToObjectData(selectedGeometry)}
            onSubmit={handleGeometryUpdate}
            addressSystemId={selectedAddressSystem.id}
            userId={currentUserId}
          />
        )}
        {/* <Group
          gap="xs"
          align="center"
          justify="center"
          p="sm"
          style={{
            position: "fixed",
            bottom: "10px",
            right: "10px",
            zIndex: 10,
          }}
          visibleFrom="md"
        >
          <Tooltip label={getTranslation("LABEL_SWITCH_LAYER") || "Switch layer"}>
            <ActionIcon onClick={handleLayerSwitch} variant="default" size="xl">
              <IconStackForward />
            </ActionIcon>
          </Tooltip>
        </Group> */}
      </AppShell.Main>
    </>
  );
}
